import { Image, VStack, useToast } from '@chakra-ui/react';
import { safeRedirect } from '@jurnee/common/src/browser/safeRedirect';
import { InputFormControl } from '@jurnee/common/src/components/InputFormControl';
import { Loader } from '@jurnee/common/src/components/Loader';
import { PrimaryButton } from '@jurnee/common/src/components/primitives/buttons/PrimaryButton';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useSearchParams } from 'react-router-dom';
import { getIsAuthenticatedSelector } from 'store/auth/auth.selectors';
import { connectAs, login } from 'store/auth/auth.thunks';
import { sendMagicLink } from '../api/auth';
import { useAppDispatch } from '../store';

export function Login() {
  const isAuthenticated = useSelector(getIsAuthenticatedSelector);
  const dispatch = useAppDispatch();
  const toast = useToast();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [linkTokens] = useState(() => {
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const connectAsToken = hashParams.get('connectAs');
    const magicToken = searchParams.get('magic-token');

    if (connectAsToken) {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }

    return { connectAsToken, magicToken };
  });
  const { connectAsToken, magicToken } = linkTokens;
  const [isCompletingConnectAs, setIsCompletingConnectAs] = useState(Boolean(connectAsToken));

  const completeConnectAs = async (token: string): Promise<void> => {
    try {
      await dispatch(connectAs(token)).unwrap();
    } catch {
      setIsCompletingConnectAs(false);
    }
  };

  useEffect(() => {
    if (connectAsToken) {
      completeConnectAs(connectAsToken);
    } else if (magicToken) {
      dispatch(login(magicToken));
    }
  }, [connectAsToken, magicToken, dispatch]);

  if (isCompletingConnectAs || magicToken) {
    return <Loader />;
  }

  if (isAuthenticated) {
    return <Navigate to={safeRedirect(searchParams.get('redirect'))} replace />;
  }

  async function onClick() {
    try {
      await sendMagicLink(email);

      toast({
        title: 'Magic link sent',
        status: 'success',
        duration: 5000,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: 'An error occured',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  }

  return (
    <VStack justifyContent="center" alignItems="center" w="100%" h="100%" spacing={10}>
      <Image src="/assets/images/logo_full.svg" w={200} />

      <VStack w={400} spacing={5} bg="white" p={5} border="1px solid" borderColor="gray.200" borderRadius={8}>
        <InputFormControl size="md" name="email" value={email} type="email" onChange={setEmail}/>
        <PrimaryButton w="100%" onClick={onClick}>Sign in</PrimaryButton>
      </VStack>
    </VStack>
  );
}